import React, { Suspense, memo, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useApprovalStatus } from '@/hooks/useApprovalStatus';
import { LoadingSpinner } from './LoadingSpinner';

interface OptimizedRouteWrapperProps {
  children: React.ReactNode;
  requiresAuth?: boolean;
  redirectTo?: string;
}

// Optimized loader with better UX
const OptimizedRouteLoader = memo(() => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="text-center space-y-4">
      <LoadingSpinner size="lg" />
      <p className="text-sm text-muted-foreground animate-pulse">Loading...</p>
    </div>
  </div>
));

const ProtectedRoute = memo(({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();
  const { hasProfile, isApproved, loading: approvalLoading } = useApprovalStatus();
  
  // Early return for loading state
  if (loading || approvalLoading) {
    return <OptimizedRouteLoader />;
  }
  
  // Early return for unauthenticated users
  if (!user) {
    return <Navigate to="/auth" replace />;
  }
  
  // Check if user has profile and is approved
  if (!hasProfile || !isApproved) {
    return <Navigate to="/pending-approval" replace />;
  }
  
  return <>{children}</>;
});

export const OptimizedRouteWrapper = memo(({ 
  children, 
  requiresAuth = false, 
  redirectTo 
}: OptimizedRouteWrapperProps) => {
  // Early return for redirects
  if (redirectTo) {
    return <Navigate to={redirectTo} replace />;
  }

  // Memoize the content to prevent unnecessary re-renders
  const content = useMemo(() => (
    <Suspense fallback={<OptimizedRouteLoader />}>
      {children}
    </Suspense>
  ), [children]);

  return requiresAuth ? (
    <ProtectedRoute>{content}</ProtectedRoute>
  ) : (
    content
  );
});

OptimizedRouteLoader.displayName = 'OptimizedRouteLoader';
ProtectedRoute.displayName = 'ProtectedRoute';
OptimizedRouteWrapper.displayName = 'OptimizedRouteWrapper';